(() => {
  const collectionSelector = '[data-raj-collection]';
  const layoutButtonSelector = '[data-raj-collection-layout-button]';
  const inputSelector = '[data-raj-filter-input]';
  const itemSelector = '[data-raj-filter-item]';
  const clearSelector = '[data-raj-filter-clear]';
  const countSelector = '[data-raj-visible-count]';
  const sortSelector = '[data-raj-sort-select]';
  const mobileToggleSelector = '[data-raj-filter-mobile-toggle]';
  const mobileCloseSelector = '[data-raj-filter-mobile-close]';
  const mobileSortToggleSelector = '[data-raj-sort-mobile-toggle]';
  const mobileSortCloseSelector = '[data-raj-sort-mobile-close]';
  const mobileSortOptionSelector = '[data-raj-sort-mobile-option]';

  const allowedLayouts = ['editorial', 'classic', 'compact'];
  const filterGroups = ['colour', 'category', 'size', 'fabric', 'occasion', 'material', 'neckline'];

  const colourMap = [
    ['black', '#000000'],
    ['white', '#ffffff'],
    ['cream', '#ead8a8'],
    ['beige', '#c7b299'],
    ['brown', '#5a2222'],
    ['maroon', '#641f23'],
    ['wine', '#6f1d2e'],
    ['red', '#b51445'],
    ['pink', '#f1c9d8'],
    ['green', '#08a64b'],
    ['blue', '#0a64c7'],
    ['navy', '#203e98'],
    ['grey', '#858585'],
    ['gray', '#858585'],
    ['gold', '#e8d49d'],
    ['taupe', '#998b7a'],
  ];

  const normalize = (value) => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = String(value || '');

    return textarea.value
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  };

  const getSwatchColor = (label) => {
    const normalized = normalize(label);
    const match = colourMap.find(([name]) => normalized.includes(name));
    return match ? match[1] : '#e6e6e6';
  };

  const splitValues = (raw) => {
    return String(raw || '')
      .split('||')
      .map((value) => value.replace(/\s+/g, ' ').trim())
      .filter(Boolean);
  };

  const readItemValues = (item, group) => {
    return splitValues(item.getAttribute(`data-raj-filter-${group}`));
  };

  const getParamName = (group) => `filter_${group}`;

  const unique = (items) => Array.from(new Set(items));

  const setLayout = (root, layout, persist = true) => {
    if (!root || !allowedLayouts.includes(layout)) return;

    root.setAttribute('data-raj-collection-layout', layout);

    root.querySelectorAll(layoutButtonSelector).forEach((button) => {
      const isActive = button.getAttribute('data-raj-collection-layout-button') === layout;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    if (!persist) return;

    const storageKey = root.getAttribute('data-raj-collection-storage-key');

    if (!storageKey) return;

    try {
      window.localStorage.setItem(storageKey, layout);
    } catch (error) {
      // Storage can fail in private mode; layout switching should still work.
    }
  };

  const buildFilterData = (root) => {
    const data = {};

    filterGroups.forEach((group) => {
      data[group] = new Map();
    });

    root.querySelectorAll(itemSelector).forEach((item) => {
      filterGroups.forEach((group) => {
        const productValues = unique(readItemValues(item, group).map(normalize)).filter(Boolean);

        productValues.forEach((normalizedValue) => {
          const originalLabel = readItemValues(item, group).find((value) => normalize(value) === normalizedValue) || normalizedValue;
          const current = data[group].get(normalizedValue) || { label: originalLabel, count: 0 };
          current.count += 1;
          data[group].set(normalizedValue, current);
        });
      });
    });

    return data;
  };

  const createOption = (root, group, valueKey, valueData, index) => {
    const label = document.createElement('label');
    label.className = `raj-filter-choice raj-filter-choice--${group}`;

    const input = document.createElement('input');
    input.type = 'checkbox';
    input.name = `raj-filter-${group}[]`;
    input.value = valueData.label;
    input.id = `RajFilter-${group}-${index}-${Math.random().toString(36).slice(2, 8)}`;
    input.setAttribute('data-raj-filter-input', '');
    input.setAttribute('data-raj-filter-group', group);
    input.setAttribute('data-raj-filter-value', valueData.label);

    const box = document.createElement('span');
    box.className = 'raj-filter-choice__box';
    box.setAttribute('aria-hidden', 'true');

    label.appendChild(input);
    label.appendChild(box);

    if (group === 'colour') {
      const swatch = document.createElement('span');
      swatch.className = 'raj-filter-choice__swatch';
      swatch.style.setProperty('--raj-filter-swatch', getSwatchColor(valueData.label));
      swatch.setAttribute('aria-hidden', 'true');
      label.appendChild(swatch);
    }

    const text = document.createElement('span');
    text.className = 'raj-filter-choice__text';
    text.textContent = valueData.label;
    label.appendChild(text);

    const count = document.createElement('span');
    count.className = 'raj-filter-choice__count';
    count.textContent = `(${valueData.count})`;
    label.appendChild(count);

    return label;
  };

  const buildFilterMarkup = (root) => {
    const data = buildFilterData(root);
    let hasAnyValues = false;

    filterGroups.forEach((group) => {
      const values = Array.from(data[group].entries()).sort((a, b) => {
        return a[1].label.localeCompare(b[1].label, undefined, { numeric: true, sensitivity: 'base' });
      });

      if (values.length) {
        hasAnyValues = true;
      }

      root.querySelectorAll(`[data-raj-filter-block="${group}"]`).forEach((block) => {
        block.hidden = values.length === 0;
      });

      root.querySelectorAll(`[data-raj-filter-options="${group}"]`).forEach((container) => {
        container.innerHTML = '';

        values.forEach(([valueKey, valueData], index) => {
          container.appendChild(createOption(root, group, valueKey, valueData, index + 1));
        });
      });
    });

    root.querySelectorAll('[data-raj-filter-empty]').forEach((node) => {
      node.hidden = hasAnyValues;
    });
  };

  const getSelectedFilters = (root) => {
    const selected = {};

    filterGroups.forEach((group) => {
      selected[group] = [];
    });

    root.querySelectorAll(`${inputSelector}:checked`).forEach((input) => {
      const group = input.getAttribute('data-raj-filter-group');
      const value = input.value;

      if (!group || !selected[group]) return;

      const normalizedValue = normalize(value);
      if (normalizedValue && !selected[group].some((existing) => normalize(existing) === normalizedValue)) {
        selected[group].push(value);
      }
    });

    return selected;
  };

  const hasActiveFilters = (selected) => {
    return filterGroups.some((group) => selected[group] && selected[group].length > 0);
  };

  const itemMatches = (item, selected) => {
    return filterGroups.every((group) => {
      const selectedValues = selected[group] || [];

      if (!selectedValues.length) return true;

      const itemValues = readItemValues(item, group).map(normalize);
      if (!itemValues.length) return false;

      return selectedValues.some((selectedValue) => itemValues.includes(normalize(selectedValue)));
    });
  };

  const updateUrlParams = (selected) => {
    const url = new URL(window.location.href);

    filterGroups.forEach((group) => {
      url.searchParams.delete(getParamName(group));
      (selected[group] || []).forEach((value) => {
        url.searchParams.append(getParamName(group), value);
      });
    });

    url.searchParams.delete('page');
    window.history.replaceState({}, '', url.toString());
  };

  const restoreFiltersFromUrl = (root) => {
    const params = new URLSearchParams(window.location.search);

    filterGroups.forEach((group) => {
      const values = params.getAll(getParamName(group)).map(normalize);
      if (!values.length) return;

      root.querySelectorAll(`${inputSelector}[data-raj-filter-group="${group}"]`).forEach((input) => {
        input.checked = values.includes(normalize(input.value));
      });
    });
  };

  const syncMatchingInputs = (root, sourceInput) => {
    const group = sourceInput.getAttribute('data-raj-filter-group');
    const value = normalize(sourceInput.value);

    if (!group || !value) return;

    root.querySelectorAll(`${inputSelector}[data-raj-filter-group="${group}"]`).forEach((input) => {
      if (normalize(input.value) === value) {
        input.checked = sourceInput.checked;
      }
    });
  };

  const updateSelectedCounts = (root, selected) => {
    filterGroups.forEach((group) => {
      const selectedCount = selected[group] ? selected[group].length : 0;

      root.querySelectorAll(`[data-raj-filter-selected-count="${group}"]`).forEach((node) => {
        if (selectedCount > 0) {
          node.hidden = false;
          node.textContent = selectedCount;
        } else {
          node.hidden = true;
          node.textContent = '';
        }
      });
    });
  };

  const updateClearButtons = (root, selected) => {
    const active = hasActiveFilters(selected);

    root.querySelectorAll(clearSelector).forEach((button) => {
      button.hidden = !active;
    });
  };

  const updateCount = (root, visibleCount, totalCount) => {
    root.querySelectorAll(countSelector).forEach((node) => {
      const count = Number.isFinite(visibleCount) ? visibleCount : totalCount;
      node.textContent = `${count} ${count === 1 ? 'PRODUCT' : 'PRODUCTS'}`;
    });
  };

  const applyFilters = (root, updateUrl = true) => {
    const selected = getSelectedFilters(root);
    const items = Array.from(root.querySelectorAll(itemSelector));
    const emptyState = root.querySelector('[data-raj-collection-empty]');
    let visibleCount = 0;

    items.forEach((item) => {
      const matched = itemMatches(item, selected);
      item.hidden = !matched;
      item.classList.toggle('raj-filter-hidden', !matched);

      if (matched) visibleCount += 1;
    });

    if (emptyState) {
      emptyState.hidden = visibleCount !== 0;
    }

    updateCount(root, visibleCount, items.length);
    updateSelectedCounts(root, selected);
    updateClearButtons(root, selected);

    if (updateUrl) {
      updateUrlParams(selected);
    }
  };

  const clearFilters = (root) => {
    root.querySelectorAll(inputSelector).forEach((input) => {
      input.checked = false;
    });

    applyFilters(root, true);
  };

  const initSort = (root) => {
    root.querySelectorAll(sortSelector).forEach((select) => {
      if (select.dataset.rajSortReady === 'true') return;
      select.dataset.rajSortReady = 'true';

      select.addEventListener('change', () => {
        goToSort(select.value);
      });
    });
  };

  const openMobileDrawer = (root, toggle) => {
    const drawer = root.querySelector('[data-raj-filter-mobile-drawer]');
    if (!drawer) return;

    drawer.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('raj-filter-drawer-open');

    if (toggle) {
      toggle.setAttribute('aria-expanded', 'true');
    }
  };

  const closeMobileDrawer = (root) => {
    const drawer = root.querySelector('[data-raj-filter-mobile-drawer]');
    if (!drawer) return;

    drawer.hidden = true;
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('raj-filter-drawer-open');

    root.querySelectorAll(mobileToggleSelector).forEach((toggle) => {
      toggle.setAttribute('aria-expanded', 'false');
    });
  };

  const openMobileSort = (root, toggle) => {
    const drawer = root.querySelector('[data-raj-sort-mobile-drawer]');
    if (!drawer) return;

    drawer.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('raj-sort-drawer-open');

    if (toggle) {
      toggle.setAttribute('aria-expanded', 'true');
    }
  };

  const closeMobileSort = (root) => {
    const drawer = root.querySelector('[data-raj-sort-mobile-drawer]');
    if (!drawer) return;

    drawer.hidden = true;
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('raj-sort-drawer-open');

    root.querySelectorAll(mobileSortToggleSelector).forEach((toggle) => {
      toggle.setAttribute('aria-expanded', 'false');
    });
  };

  const goToSort = (value) => {
    const url = new URL(window.location.href);

    if (value) {
      url.searchParams.set('sort_by', value);
    } else {
      url.searchParams.delete('sort_by');
    }

    url.searchParams.delete('page');
    window.location.href = url.toString();
  };

  const initCollection = (root) => {
    if (!root || root.dataset.rajCollectionReady === 'true') return;
    root.dataset.rajCollectionReady = 'true';

    const storageKey = root.getAttribute('data-raj-collection-storage-key');
    const defaultLayout = root.getAttribute('data-raj-collection-default-layout') || 'classic';
    let savedLayout = null;

    if (storageKey) {
      try {
        savedLayout = window.localStorage.getItem(storageKey);
      } catch (error) {
        savedLayout = null;
      }
    }

    setLayout(root, allowedLayouts.includes(savedLayout) ? savedLayout : defaultLayout, false);
    buildFilterMarkup(root);
    restoreFiltersFromUrl(root);
    applyFilters(root, false);
    initSort(root);

    root.addEventListener('click', (event) => {
      const layoutButton = event.target.closest(layoutButtonSelector);
      const clearButton = event.target.closest(clearSelector);
      const mobileToggle = event.target.closest(mobileToggleSelector);
      const mobileClose = event.target.closest(mobileCloseSelector);
      const mobileSortToggle = event.target.closest(mobileSortToggleSelector);
      const mobileSortClose = event.target.closest(mobileSortCloseSelector);
      const mobileSortOption = event.target.closest(mobileSortOptionSelector);

      if (layoutButton && root.contains(layoutButton)) {
        event.preventDefault();
        setLayout(root, layoutButton.getAttribute('data-raj-collection-layout-button'), true);
        return;
      }

      if (clearButton && root.contains(clearButton)) {
        event.preventDefault();
        clearFilters(root);
        return;
      }

      if (mobileToggle && root.contains(mobileToggle)) {
        event.preventDefault();
        openMobileDrawer(root, mobileToggle);
        return;
      }

      if (mobileClose && root.contains(mobileClose)) {
        event.preventDefault();
        closeMobileDrawer(root);
        return;
      }

      if (mobileSortToggle && root.contains(mobileSortToggle)) {
        event.preventDefault();
        openMobileSort(root, mobileSortToggle);
        return;
      }

      if (mobileSortClose && root.contains(mobileSortClose)) {
        event.preventDefault();
        closeMobileSort(root);
        return;
      }

      if (mobileSortOption && root.contains(mobileSortOption)) {
        event.preventDefault();
        goToSort(mobileSortOption.getAttribute('data-raj-sort-value') || '');
      }
    });

    root.addEventListener('change', (event) => {
      const input = event.target.closest(inputSelector);
      if (!input || !root.contains(input)) return;

      syncMatchingInputs(root, input);
      applyFilters(root, true);
    });
  };

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    document.querySelectorAll(collectionSelector).forEach((root) => {
      closeMobileDrawer(root);
      closeMobileSort(root);
    });
  });

  const initAll = () => {
    document.querySelectorAll(collectionSelector).forEach(initCollection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);

  window.RajCollectionLayoutRefresh = initAll;
  window.RajCollectionFiltersRefresh = initAll;
})();
